import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { Typography } from "@mui/material";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

import messages from "../../../../../assets/locale/messages";
import {
  UIActionModal,
  UIButton,
  UICallout,
  UIFormSectionCard,
} from "../../../../ui";
import { createCompanyPortalPaymentRequest } from "../../../../../network/apis/companyPortalEvents";
import { getPortalApiErrorMessage } from "../../../../../utils/companyPortal/eventApi";
import { showErrorMsg } from "../../../../../utils/Helpers";
import EventSummaryBox from "../../Modals/PublishEventModal/EventSummaryBox";
import { getSubscriptionPlanName } from "../ChooseCrewSize/crewSizeUtils";
import EventSummaryCard from "./EventSummaryCard";
import BankTransferDetails from "./BankTransferDetails";
import PaymentMethodField from "./PaymentMethodField";
import TransferEvidence from "./TransferEvidence";
import { PAYMENT_METHODS } from "./publishPayConstants";
import useBankAccountInfo from "./useBankAccountInfo";
import useEvidenceUpload, { UPLOAD_STATUS } from "./useEvidenceUpload";

import "./PublishAndPay.scss";

const DRAFT_MODALS = {
  draft: "draftModal",
  uploading: "uploadingModal",
};

const PublishAndPay = ({
  initialEvent,
  eventScheduleBounds,
  isActive,
  registerStepApi,
  crewSizeOption,
  onPaymentSubmitted,
  onPaymentRequestFailed,
  onSaveAsDraft,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { events, shared, clientReports, inputsValidations } =
    messages[lang] ?? messages.en;
  const t = events.creatEvent.publishPay;

  const login = useSelector((state) => state.auth?.login);
  const subscriptionPlan =
    initialEvent?.company?.subscription_plan ?? login?.company?.subscription_plan;
  const upload = useEvidenceUpload({
    // The upload calls skip the interceptor's toast, so the error is shown here.
    onError: (error) =>
      showErrorMsg(
        error?.response ? shared.generalErrMsg : shared.noInternetConnection,
      ),
    // The proof saved with the draft (PROLINE-4165), when the event carries it.
    savedUrl: initialEvent?.payment_proof_url,
  });
  const evidenceUrl = upload.url;
  const eventId = initialEvent?.id;
  const crewSizePlanId = crewSizeOption?.id;

  const bankDetails = useBankAccountInfo(isActive);
  const isUploaded = upload.status === UPLOAD_STATUS.uploaded;
  const isUploading = upload.status === UPLOAD_STATUS.uploading;

  /** "Submit for Approval" stays enabled: pressing it with no file marks the upload as missing. */
  const [showEvidenceMissing, setShowEvidenceMissing] = useState(false);
  const isEvidenceMissing = showEvidenceMissing && upload.status === UPLOAD_STATUS.idle;

  /** Which "Save As Draft" confirmation is open; resolved through the step API promise. */
  const [draftModal, setDraftModal] = useState(null);
  const draftResolveRef = useRef(null);
  /** What was entered so far (event name, logo, staff requested), shown in that confirmation. */
  const [draftSummary, setDraftSummary] = useState();
  const [submittedOpen, setSubmittedOpen] = useState(false);
  /** "Submit for Approval?" confirmation, resolved through the step API promise like the draft one. */
  const [submitConfirmOpen, setSubmitConfirmOpen] = useState(false);
  const submitResolveRef = useRef(null);
  /** Upload-in-progress warning when the user clicks "Submit for Approval" while still uploading. */
  const [submitUploadingOpen, setSubmitUploadingOpen] = useState(false);
  const submitUploadingResolveRef = useRef(null);

  useEffect(() => {
    if (!registerStepApi) return undefined;
    registerStepApi({
      // The event itself is saved on the earlier steps; this one only raises the request.
      skipPersist: true,
      validateContinue: async () => {
        // Block only when no file was started; uploading proceeds so submitForApproval
        // can intercept and show the "upload still in progress" modal.
        const idle = upload.status === UPLOAD_STATUS.idle;
        if (idle) setShowEvidenceMissing(true);
        return !idle;
      },
      // A draft keeps the proof, under the same key as the publishing request (PROLINE-4165).
      // While it is still uploading there is no link yet: the saved one is left as it is.
      buildPayload: async ({ mode } = {}) =>
        mode === "draft" && !isUploading
          ? { payment_proof_url: evidenceUrl ?? null }
          : {},
      confirmDraft: (eventSummary) =>
        new Promise((resolve) => {
          draftResolveRef.current = resolve;
          setDraftSummary(eventSummary);
          setDraftModal(isUploading ? DRAFT_MODALS.uploading : DRAFT_MODALS.draft);
        }),
      // The backend prices the request from the crew size plan; no amount is sent.
      submitForApproval: async (eventSummary) => {
        if (isUploading) {
          setDraftSummary(eventSummary);
          const saveAsDraft = await new Promise((resolve) => {
            submitUploadingResolveRef.current = resolve;
            setSubmitUploadingOpen(true);
          });
          if (saveAsDraft) onSaveAsDraft?.();
          return;
        }
        const confirmed = await new Promise((resolve) => {
          submitResolveRef.current = resolve;
          setSubmitConfirmOpen(true);
        });
        if (!confirmed) return;
        try {
          await createCompanyPortalPaymentRequest(
            {
              event_id: eventId,
              crew_size_plan_id: crewSizePlanId,
              payment_type: PAYMENT_METHODS.offline,
              payment_proof_url: evidenceUrl,
            },
            // The error is toasted once below, not also by the interceptor.
            { handlerEnabled: false },
          );
          setSubmittedOpen(true);
        } catch (error) {
          // The reason the backend gives: incomplete event, request already pending, ...
          showErrorMsg(getPortalApiErrorMessage(error, shared.generalErrMsg));
          onPaymentRequestFailed?.();
        }
      },
    });
    return () => registerStepApi(null);
  }, [
    registerStepApi,
    isUploaded,
    isUploading,
    upload.status,
    eventId,
    crewSizePlanId,
    evidenceUrl,
    shared.generalErrMsg,
    onSaveAsDraft,
  ]);

  const resolveDraft = (confirmed) => {
    setDraftModal(null);
    draftResolveRef.current?.(confirmed);
    draftResolveRef.current = null;
  };

  const resolveSubmit = (confirmed) => {
    setSubmitConfirmOpen(false);
    submitResolveRef.current?.(confirmed);
    submitResolveRef.current = null;
  };

  const resolveSubmitUploading = (saveAsDraft) => {
    setSubmitUploadingOpen(false);
    submitUploadingResolveRef.current?.(saveAsDraft);
    submitUploadingResolveRef.current = null;
  };

  const draftCopy = draftModal ? t[draftModal] : null;

  return (
    <div className="publish-pay-step">
      <div>
        <Typography component="h1" className="text-heading-xl publish-pay-step__title">
          {t.title}
        </Typography>
        <Typography component="p" className="text-body-sm publish-pay-step__subtitle">
          {t.subtitle}
        </Typography>
      </div>

      <div className="publish-pay-step__layout">
        <div className="publish-pay-step__column">
          <EventSummaryCard
            t={t}
            crewSizeT={events.creatEvent.crewSize}
            eventName={initialEvent?.name}
            startAt={
              eventScheduleBounds?.eventStartAt ?? initialEvent?.event_start_at
            }
            endAt={eventScheduleBounds?.eventEndAt ?? initialEvent?.event_end_at}
            event={initialEvent}
            crewSizeOption={crewSizeOption}
            planName={getSubscriptionPlanName(subscriptionPlan, lang)}
          />
          <BankTransferDetails t={t} details={bankDetails} />
        </div>

        <div className="publish-pay-step__column">
          <UIFormSectionCard
            title={t.paymentMethod}
            className="publish-pay-step__card"
          >
            <PaymentMethodField t={t} />
            <TransferEvidence
              t={t}
              shared={shared}
              previewT={clientReports.previewModal}
              isRtl={lang === "ar"}
              upload={upload}
              errorText={
                isEvidenceMissing ? inputsValidations.generalFieldRequired : undefined
              }
            />
          </UIFormSectionCard>

          <UICallout
            variant="warning"
            className="publish-pay-step__notice"
            icon={<WarningAmberRoundedIcon fontSize="small" />}
            title={t.approvalNoticeTitle}
            description={t.approvalNoticeMessage}
          />
        </div>
      </div>

      <UIActionModal
        open={Boolean(draftModal)}
        variant="info"
        maxWidth="xs"
        title={draftCopy?.title}
        onClose={() => resolveDraft(false)}
        actions={
          <UIActionModal.Actions
            cancelLabel={draftCopy?.keepLabel ?? ""}
            confirmLabel={events.creatEvent.saveAsDraft}
            onCancel={() => resolveDraft(false)}
            onConfirm={() => resolveDraft(true)}
          />
        }
      >
        <Typography component="p" className="text-body-sm publish-pay-step__modal-message">
          {draftCopy?.message}
        </Typography>
        <EventSummaryBox eventSummary={draftSummary} />
      </UIActionModal>

      <UIActionModal
        open={submitConfirmOpen}
        variant="info"
        maxWidth="xs"
        title={t.submitConfirmModal.title}
        onClose={() => resolveSubmit(false)}
        actions={
          <UIActionModal.Actions
            cancelLabel={shared.cancel}
            confirmLabel={t.submitForApproval}
            onCancel={() => resolveSubmit(false)}
            onConfirm={() => resolveSubmit(true)}
          />
        }
      >
        <Typography component="p" className="text-body-sm publish-pay-step__modal-message">
          {t.submitConfirmModal.message}
        </Typography>
      </UIActionModal>

      <UIActionModal
        open={submittedOpen}
        variant="info"
        maxWidth="xs"
        title={t.submittedModal.title}
        // The payment is already sent: no Cancel, so the company cannot stay on Publish & Pay.
        actions={
          <UIButton
            appearance="gradient"
            fullWidth
            onClick={() => {
              setSubmittedOpen(false);
              onPaymentSubmitted?.();
            }}
          >
            {t.submittedModal.gotIt}
          </UIButton>
        }
      >
        <Typography component="p" className="text-body-sm publish-pay-step__modal-message">
          {t.submittedModal.message}
        </Typography>
      </UIActionModal>

      <UIActionModal
        open={submitUploadingOpen}
        variant="info"
        maxWidth="xs"
        title={t.uploadingModal.title}
        onClose={() => resolveSubmitUploading(false)}
        actions={
          <UIActionModal.Actions
            cancelLabel={t.uploadingModal.keepLabel}
            confirmLabel={events.creatEvent.saveAsDraft}
            onCancel={() => resolveSubmitUploading(false)}
            onConfirm={() => resolveSubmitUploading(true)}
          />
        }
      >
        <Typography component="p" className="text-body-sm publish-pay-step__modal-message">
          {t.uploadingModal.message}
        </Typography>
        <EventSummaryBox eventSummary={draftSummary} />
      </UIActionModal>
    </div>
  );
};

PublishAndPay.propTypes = {
  initialEvent: PropTypes.object,
  eventScheduleBounds: PropTypes.shape({
    eventStartAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    eventEndAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  /** The wizard is on this step. */
  isActive: PropTypes.bool,
  registerStepApi: PropTypes.func,
  /** Crew size of the event, resolved by the shared form (`toCrewSizeOption`). */
  crewSizeOption: PropTypes.object,
  /** Called after the company acknowledges the "Submitted for Approval" popup. */
  onPaymentSubmitted: PropTypes.func,
  /** The request was refused: the page checks whether the event is still a draft. */
  onPaymentRequestFailed: PropTypes.func,
  /** Called when the user chooses "Save As Draft" from the upload-in-progress modal during submit. */
  onSaveAsDraft: PropTypes.func,
};

PublishAndPay.defaultProps = {
  initialEvent: undefined,
  eventScheduleBounds: undefined,
  isActive: true,
  registerStepApi: undefined,
  crewSizeOption: null,
  onPaymentSubmitted: undefined,
  onPaymentRequestFailed: undefined,
  onSaveAsDraft: undefined,
};

export default PublishAndPay;
